import React, { useState, useEffect, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, Container, Row, Col, Button } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import OptionFormModal from "./OptionFormModal";
import { RoleContext } from "../../RoleContext";
import { getSeverityText, QUESTION_TYPES, SEVERITY_LEVELS } from "../../enum";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Box, Typography, CircularProgress } from '@mui/material';

const QuestionDetail = () => {
  const roleData = useContext(RoleContext);
  const role = JSON.parse(roleData);

  const { questionId } = useParams();

  const [question, setQuestion] = useState(null);
  const [options, setOptions] = useState([]);

  /* option-form modal */
  const [showOptionModal, setShowOptionModal] = useState(false);
  const [currentOption, setCurrentOption] = useState(null);

  /* delete-confirmation dialog */
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [optionToDelete, setOptionToDelete] = useState(null);

  const [showGuideModal, setShowGuideModal] = useState(false);
  const [guideLoading, setGuideLoading] = useState(false);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [guideError, setGuideError] = useState(null);

  /* ───────────────────────────────────────────── fetch helpers ── */
  const fetchQuestionDetails = async () => {
    try {
      const { data } = await axios.get(`/questions/${questionId}`);
      setQuestion(data);

      const sorted = (data.question_options || [])
        .map((o) => ({ ...o, specialities: o.specialities || [] }))
        .sort((a, b) => a.display_order - b.display_order);

      setOptions(sorted);
    } catch (err) {
      toast.error("Failed to fetch question details.");
    }
  };

  const updateOptionsOrder = async (reordered) => {
    try {
      await axios.put(`/questions/${questionId}/options/reorder`, {
        ordered_ids: reordered.map((o) => o.option_id),
      });
      toast.success("Option order updated successfully.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update option order – reverting.");
      fetchQuestionDetails(); // rollback to DB state
    }
  };

  /* ───────────────────────────────────────────── handlers ─────── */
  /* drag-and-drop */
  const onDragEnd = async (result) => {
    if (!result.destination) return;

    const reordered = Array.from(options);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    setOptions(reordered);
    await updateOptionsOrder(reordered);
  };

  /* option-form modal */
  const handleOpenOptionModal = (opt = null) => {
    setCurrentOption(opt);
    setShowOptionModal(true);
  };
  const handleCloseOptionModal = () => {
    setShowOptionModal(false);
    setCurrentOption(null);
    fetchQuestionDetails();
  };

  /* delete confirmation dialog */
  const handleConfirmDelete = (opt) => {
    setOptionToDelete(opt);
    setDeleteDialogOpen(true);
  };
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setOptionToDelete(null);
  };
  const handleDeleteOption = async () => {
    if (!optionToDelete) return;
    try {
      if (options.length <= 2) {
        toast.error("Cannot delete. A minimum of two options is required.");
        return;
      }
      await axios.delete(`/options/${optionToDelete.option_id}`);
      toast.success("Option deleted successfully.");
      handleCloseDeleteDialog();
      fetchQuestionDetails();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete option.");
      handleCloseDeleteDialog();
    }
  };

  /* Fetch a First Aid Guide by ID */
  const fetchGuideById = async (guideId) => {
    setGuideLoading(true);
    setGuideError(null);
    setSelectedGuide(null);
    try {
      const { data } = await axios.get(`/first-aid-guides/${guideId}`);
      setSelectedGuide(data);
    } catch (err) {
      setGuideError('Failed to load First Aid Guide.');
    } finally {
      setGuideLoading(false);
    }
  };

  /* Helper for rendering step content/media (copied from FirstAidGuideForm.js) */
  const renderStepContent = (step) => {
    if (step.mediaType === 'image' && step.mediaUrl) {
      const fullImageUrl = step.mediaUrl.startsWith('http') 
        ? step.mediaUrl 
        : `${process.env.REACT_APP_BASE_URL || 'http://localhost:3005'}${step.mediaUrl}`;
      return (
        <Box sx={{ position: 'relative', borderRadius: '0.5rem', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
          <img src={fullImageUrl} alt="First Aid Step" style={{ maxWidth: '100%', maxHeight: 160, objectFit: 'cover', display: 'block' }} />
        </Box>
      );
    }
    if (step.mediaType === 'video' && step.mediaUrl) {
      const fullVideoUrl = step.mediaUrl.startsWith('http') 
        ? step.mediaUrl 
        : `${process.env.REACT_APP_BASE_URL || 'http://localhost:3005'}${step.mediaUrl}`;
      return (
        <Box sx={{ position: 'relative', borderRadius: '0.5rem', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
          <video controls style={{ maxWidth: '100%', maxHeight: 180, objectFit: 'cover', display: 'block' }}>
            <source src={fullVideoUrl} />
            Your browser does not support the video tag.
          </video>
        </Box>
      );
    }
    return (
      <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.6, color: '#374151' }}>{step.content}</Typography>
    );
  };

  /* lifecycle */
  useEffect(() => {
    fetchQuestionDetails();
  }, [questionId]);

  /* guard */
  if (!question) return <div className="text-center my-4">Loading…</div>;

  const isYesNo = question.type === QUESTION_TYPES.YES_NO;

  return (
    <div className="content-wrappers">
      <ToastContainer />

      {/* ───────── header ───────── */}
      <section className="content-header">
        <div className="container-fluid">
          <Row className="mb-2">
            <Col sm={6}>
              <h1>Question Details</h1>
            </Col>
            <Col sm={6}>
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <Link to="/ambulance_lookup">Home</Link>
                </li>
                <li className="breadcrumb-item active">Question Detail</li>
              </ol>
            </Col>
          </Row>
        </div>
      </section>

      {/* ───────── main content ───────── */}
      <section className="content">
        <Container fluid>
          <Row>
            {/* left: question info */}
            <Col md={4}>
              <Card>
                <Card.Header>
                  <h3 className="card-title">Question Details</h3>
                </Card.Header>
                <Card.Body>
                  <p>
                    <b>Text:</b> {question.text}
                  </p>
                  <p>
                    <b>Type:</b> {isYesNo ? "Yes/No" : "Multiple Choice"}
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* right: options list */}
            <Col md={8}>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h3 className="card-title">Options (Drag to reorder)</h3>
                  {!isYesNo && (
                    <Button
                      className="btn btn-success btn-sm"
                      onClick={() => handleOpenOptionModal()}
                    >
                      <i className="fas fa-plus mr-1" /> Add Option
                    </Button>
                  )}
                </Card.Header>
                <Card.Body>
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="options">
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                          {options.map((opt, index) => (
                            <Draggable
                              key={opt.option_id}
                              draggableId={opt.option_id.toString()}
                              index={index}
                            >
                              {(prov, snapshot) => (
                                <div
                                  ref={prov.innerRef}
                                  {...prov.draggableProps}
                                  {...prov.dragHandleProps}
                                  className="p-2 mb-2 border rounded d-flex justify-content-between align-items-center"
                                  style={{
                                    ...prov.draggableProps.style,
                                    background: snapshot.isDragging ? "#e9ecef" : "#f8f9fa",
                                  }}
                                >
                                  <div>
                                    <strong>{opt.text}</strong>
                                    <div className="text-muted small">
                                      Severity: {getSeverityText(opt?.severity_level) || "Unknown"}
                                    </div>
                                    {/* Show First Aid Guide link if present */}
                                    {opt.link && (
                                      <div>
                                        <Button
                                          variant="link"
                                          style={{ padding: 0, fontSize: '0.95em' }}
                                          onClick={() => {
                                            setShowGuideModal(true);
                                            fetchGuideById(opt.link);
                                          }}
                                        >
                                          View First Aid Guide
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                  <div>
                                    {role.some((r) => r.perm_name === "update_question_option") && (
                                      <Link
                                        to="#"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          handleOpenOptionModal(opt);
                                        }}
                                      >
                                        <i className="fas fa-edit text-info mx-2" />
                                      </Link>
                                    )}
                                    {role.some((r) => r.perm_name === "delete_question_option") && (
                                      <Link
                                        to="#"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          handleConfirmDelete(opt);
                                        }}
                                      >
                                        <i className="fas fa-trash text-danger" />
                                      </Link>
                                    )}
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* First Aid Guide Modal */}
      <Dialog open={showGuideModal} onClose={() => setShowGuideModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>First Aid Guide</DialogTitle>
        <DialogContent>
          {guideLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
              <CircularProgress />
            </Box>
          ) : guideError ? (
            <Typography color="error">{guideError}</Typography>
          ) : selectedGuide ? (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>{selectedGuide.title}</Typography>
              {selectedGuide.steps && selectedGuide.steps.length > 0 ? (
                selectedGuide.steps.map((step, idx) => (
                  <Box key={idx} sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>{step.label}</Typography>
                    {renderStepContent(step)}
                  </Box>
                ))
              ) : (
                <Typography>No steps found for this guide.</Typography>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowGuideModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ───────── option-form modal ───────── */}
      <OptionFormModal
        show={showOptionModal}
        onHide={handleCloseOptionModal}
        option={currentOption}
        questionId={questionId}
        onSuccess={fetchQuestionDetails}
      />

      {/* ───────── delete-confirmation dialog ───────── */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this option? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteOption} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default QuestionDetail;
